import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AUTH_ROUTES, TOKEN_STORAGE_KEY, USERNAME_STORAGE_KEY } from '../authConstants';
import type { AuthAction, AuthState } from '../authTypes';
import type { ResolvedPermissions } from '../../permissions/permissionTypes';
import { ApiError } from '../../../lib/http/ApiError';
import { useNotification } from '../../notifications/hooks/useNotification';
import { AuthContext } from './AuthContext';
import { authApi } from '../authApi';

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        token: action.payload.token,
        username: action.payload.username,
        isAuthenticated: action.payload.token !== null,
        isInitialized: true,
        role: action.payload.role,
        permissions: action.payload.permissions,
      };
    case 'LOGIN':
      return {
        token: action.payload.token,
        username: action.payload.username,
        isAuthenticated: true,
        isInitialized: true,
        role: action.payload.role,
        permissions: action.payload.permissions,
      };
    case 'REFRESH_ME':
      // Drop a stale /auth/me whose token is no longer current — e.g. a logout (or
      // a re-login) that raced the in-flight request. Without this, the resolved
      // response would restore the previous user's rights into a logged-out context.
      if (action.payload.token !== state.token) return state;
      // Same rights → same state object: a 403-triggered resync (useFetch) must
      // not re-render every consumer, nor retrigger effects keyed on `permissions`.
      if (
        action.payload.role === state.role &&
        JSON.stringify(action.payload.permissions) === JSON.stringify(state.permissions)
      ) {
        return state;
      }
      return {
        ...state,
        role: action.payload.role,
        permissions: action.payload.permissions,
      };
    case 'LOGOUT':
      return {
        token: null,
        username: null,
        isAuthenticated: false,
        isInitialized: true,
        role: null,
        permissions: null,
      };
  }
}

const initialState: AuthState = {
  token: null,
  username: null,
  isAuthenticated: false,
  isInitialized: false,
  role: null,
  permissions: null,
};

type MeResult =
  | { kind: 'ok'; role: string | null; permissions: ResolvedPermissions | null }
  // 401 : compte désactivé/supprimé ou jeton expiré — la session est morte (HOMCO-1184)
  | { kind: 'unauthorized' }
  // réseau / 5xx : on garde la session, droits refusés par défaut
  | { kind: 'unavailable' };

const NO_RIGHTS = { role: null, permissions: null } as const;

// Rôle et droits absents du login/du localStorage par choix backend — /auth/me est
// l'unique source (hiérarchie déjà appliquée côté serveur). Seule une session refusée
// (401) met fin à la session ; un service indisponible ne doit jamais bloquer l'auth :
// on retombe sur `null` (droits refusés par défaut), role + droits seront redemandés
// au prochain appel (login ou montage).
function resolveMe(token: string): Promise<MeResult> {
  return authApi
    .me(token)
    .then((me): MeResult => ({ kind: 'ok', role: me.role, permissions: me.resolvedPermissions }))
    .catch(
      (e: unknown): MeResult =>
        ApiError.isApiError(e) && e.isUnauthorized
          ? { kind: 'unauthorized' }
          : { kind: 'unavailable' },
    );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { t } = useTranslation('common');
  const [state, dispatch] = useReducer(authReducer, initialState);
  const bootstrappedRef = useRef(false);
  const endingRef = useRef(false);
  const refreshingRef = useRef<Promise<void> | null>(null);

  const clearStoredSession = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USERNAME_STORAGE_KEY);
  }, []);

  const notifySessionEnded = useCallback(() => {
    notify({ message: t('auth.sessionEnded'), severity: 'warning' });
  }, [notify, t]);

  // Lecture initiale du localStorage — une seule fois au montage (le garde-fou évite
  // de relancer l'amorçage quand `t` change avec la langue).
  // Le rôle n'est jamais persisté : on le redemande à /auth/me (source unique,
  // toujours à jour côté serveur) plutôt que de risquer une copie locale obsolète.
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const username = localStorage.getItem(USERNAME_STORAGE_KEY);

    if (!token) {
      dispatch({ type: 'INITIALIZE', payload: { token, username, role: null, permissions: null } });
      return;
    }

    resolveMe(token).then(me => {
      if (me.kind === 'unauthorized') {
        // Session refusée (compte désactivé/supprimé, jeton expiré) : pas d'écran
        // « connecté » sans droits — ProtectedRoute renvoie vers /login.
        clearStoredSession();
        dispatch({
          type: 'INITIALIZE',
          payload: { token: null, username: null, ...NO_RIGHTS },
        });
        notifySessionEnded();
        return;
      }
      const { role, permissions } = me.kind === 'ok' ? me : NO_RIGHTS;
      dispatch({ type: 'INITIALIZE', payload: { token, username, role, permissions } });
    });
  }, [clearStoredSession, notifySessionEnded]);

  // Fin de session locale, déclenchée par un refus du serveur (401). Pas d'appel
  // /auth/logout : le jeton est déjà refusé et les refresh tokens ont été purgés par la
  // révocation (HOMCO-1184). Idempotent : plusieurs requêtes en vol peuvent recevoir
  // 401 en même temps.
  const endSession = useCallback(() => {
    if (endingRef.current) return;
    endingRef.current = true;
    refreshingRef.current = null;
    clearStoredSession();
    dispatch({ type: 'LOGOUT' });
    notifySessionEnded();
    navigate(AUTH_ROUTES.LOGIN, { replace: true });
  }, [clearStoredSession, navigate, notifySessionEnded]);

  const login = useCallback(
    async (token: string, username: string) => {
      endingRef.current = false;
      refreshingRef.current = null;
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(USERNAME_STORAGE_KEY, username);
      const me = await resolveMe(token);
      if (me.kind === 'unauthorized') {
        // Compte désactivé entre l'authentification et /auth/me.
        endSession();
        return;
      }
      const { role, permissions } = me.kind === 'ok' ? me : NO_RIGHTS;
      dispatch({ type: 'LOGIN', payload: { token, username, role, permissions } });
      navigate(AUTH_ROUTES.HOME);
    },
    [navigate, endSession],
  );

  const logout = useCallback(
    ({ redirect = true }: { redirect?: boolean } = {}) => {
      if (state.token) {
        authApi.logout(state.token).catch(() => {
          // fire and forget — local logout proceeds regardless of server response
        });
      }
      refreshingRef.current = null;
      clearStoredSession();
      dispatch({ type: 'LOGOUT' });
      if (redirect) navigate(AUTH_ROUTES.LOGIN);
    },
    [clearStoredSession, navigate, state.token],
  );

  // Re-fetches /auth/me (e.g. after the current user's profile or role changed) and
  // updates role + permissions in place. No-op without a token; await-before-dispatch
  // keeps a concurrent logout from being overwritten by a late response. A 401 ends
  // the session; an unavailable /auth/me keeps the rights already loaded. Single-flight:
  // N concurrent callers (e.g. N simultaneous 403s in useFetch) share one request.
  // login/logout/endSession drop the in-flight promise: a response that lands after the
  // session changed (e.g. a stale 401 of the previous user) is ignored, it must never
  // end the new session.
  const refreshPermissions = useCallback((): Promise<void> => {
    const token = state.token;
    if (!token) return Promise.resolve();
    if (refreshingRef.current) return refreshingRef.current;

    const inFlight: Promise<void> = resolveMe(token)
      .then(me => {
        if (refreshingRef.current !== inFlight) return;
        if (me.kind === 'unauthorized') {
          endSession();
          return;
        }
        if (me.kind === 'unavailable') return;
        dispatch({
          type: 'REFRESH_ME',
          payload: { token, role: me.role, permissions: me.permissions },
        });
      })
      .finally(() => {
        if (refreshingRef.current === inFlight) refreshingRef.current = null;
      });
    refreshingRef.current = inFlight;
    return inFlight;
  }, [state.token, endSession]);

  const value = useMemo(
    () => ({ ...state, login, logout, endSession, refreshPermissions }),
    [state, login, logout, endSession, refreshPermissions],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
